// The Cockpit shell (docs/cockpit/spec.md): a sidebar of collapsible groups with every page, the business
// switcher at its top and Credits, the trial's days left and Pause in its footer; a top bar with the page title, search (`/`) over
// orders, Counterparties and Documents, notifications and the account menu; and on phones a bottom tab bar
// whose Menu opens the sidebar as a drawer. The sidebar lists only the pages the person's Roles open (#513); Memory,
// Set up LAMP, Credits and usage, Users and Roles and permissions are listed for Owners only, as is the control to
// pause LAMP's agents.
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import type { ChatEntry, Decision, SearchResult, Session } from "../api.js";
import { EmailChoice, type EmailChoiceApi } from "./EmailChoice.js";
import { Icon } from "./icons.js";
import { Menu, useDismiss } from "./Menu.js";
import { otherLanguage, useLanguage, useWords } from "../i18n/words.js";
import { LegalLinks } from "../legal/Notice.js";
import { usePolling } from "./poll.js";
import { NAV, PAGES, TABS, listed, type Page, type WordKey } from "./nav.js";

const CLOSED_GROUPS = "lamp.nav.closed";
const RESULT_KIND: Readonly<Record<SearchResult["kind"], WordKey>> = {
  order: "shellOrder",
  counterparty: "shellCounterparty",
  document: "shellDocument",
};
const PHONE = "(max-width: 760px)";

function readClosed(): ReadonlySet<string> {
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(CLOSED_GROUPS) ?? "[]");
    return new Set(Array.isArray(stored) ? stored.filter((g): g is string => typeof g === "string") : []);
  } catch {
    return new Set();
  }
}

function NavLink({
  page,
  current,
  onNavigate,
  children,
}: {
  page: Page;
  current: Page;
  onNavigate: (p: Page) => void;
  children: ReactNode;
}) {
  const go = (event: MouseEvent) => {
    // Plain left clicks stay in the app; others open as the browser does.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onNavigate(page);
  };
  return (
    <a href={PAGES[page].path} aria-current={page === current ? "page" : undefined} onClick={go}>
      {children}
    </a>
  );
}

/** What the bell lists (#512): the person's Work Items from Today and LAMP's messages in their thread, polled. */
export interface Notices {
  /** The person's decision queue now. */
  load(): Promise<readonly Decision[]>;
  /** Opens one, as Today's card does. */
  open(decision: Decision): void;
  /** The person's thread now: what LAMP said there since they last looked is new (a customer's answer, an email sent). */
  messages?(): Promise<readonly ChatEntry[]>;
  /** Opens the thread, in Chat. */
  openChat?(): void;
  /** The emails the person switches on about their work, under the bell. */
  readonly emails?: EmailChoiceApi;
}

const SEEN = "lamp.bell.seen";
/** When the person last saw LAMP's messages: in the bell or in Chat. */
const READ = "lamp.bell.read";
/** How much of a message the bell shows. */
const GLIMPSE = 80;

function readRead(key: string): number {
  try {
    const stored = Number(window.localStorage.getItem(key) ?? "0");
    return Number.isFinite(stored) ? stored : 0;
  } catch {
    return 0;
  }
}

/** LAMP's messages, not cards (those are Work Items, listed as such), said after `since`. */
function messagesAfter(thread: readonly ChatEntry[], since: number): readonly ChatEntry[] {
  return thread.filter((m) => m.author === "lamp" && m.card === undefined && Date.parse(m.at) > since);
}

function glimpse(text: string): string {
  return text.length > GLIMPSE ? `${text.slice(0, GLIMPSE - 1)}…` : text;
}

function readSeen(key: string): ReadonlySet<string> {
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    return new Set(Array.isArray(stored) ? stored.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

/**
 * The bell: the person's Work Items they have not seen in it yet, those late, and LAMP's messages since they last
 * looked, with how many. Opening it marks what it lists as seen; a late one stays until it is done. While the person
 * is in Chat, the messages there are seen.
 */
function Notifications({
  notices,
  person,
  inChat,
}: {
  notices?: Notices | undefined;
  person: string;
  inChat: boolean;
}) {
  const w = useWords();
  const [open, setOpen] = useState(false);
  const [decisions, setDecisions] = useState<readonly Decision[]>([]);
  const seenKey = `${SEEN}.${person}`;
  const [seen, setSeen] = useState(() => readSeen(seenKey));
  /** What the open bell lists: as it stood when opened, though opening marked it seen. */
  const [shown, setShown] = useState<{
    fresh: readonly Decision[];
    late: readonly Decision[];
    messages: readonly ChatEntry[];
  }>({ fresh: [], late: [], messages: [] });
  const [thread, setThread] = useState<readonly ChatEntry[]>([]);
  const readKey = `${READ}.${person}`;
  const [read, setRead] = useState(() => readRead(readKey));
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useDismiss(open, wrap, () => setOpen(false));
  const load = () => {
    // A refusal or a dropped connection leaves the bell as it was; the next poll tries again.
    notices?.load().then(setDecisions, () => undefined);
    notices?.messages?.().then(setThread, () => undefined);
  };
  useEffect(load, [notices]);
  usePolling(load);

  const fresh = decisions.filter((d) => !seen.has(d.workItem));
  const late = decisions.filter((d) => d.late === true && seen.has(d.workItem));
  const messages = messagesAfter(thread, read);
  const count = fresh.length + late.length + messages.length;
  const markRead = () => {
    const last = Math.max(read, ...thread.map((m) => Date.parse(m.at)).filter(Number.isFinite));
    if (last === read) return;
    setRead(last);
    try {
      window.localStorage.setItem(readKey, String(last));
    } catch {
      // Storage may be unavailable (a private window); the bell still counts for this visit.
    }
  };
  // In Chat the person reads the thread itself.
  useEffect(() => {
    if (inChat) markRead();
  });
  const openBell = () => {
    setShown({ fresh, late, messages });
    markRead();
    // Only what is still waiting is kept, so the list never outgrows the queue.
    const next = new Set(decisions.map((d) => d.workItem));
    setSeen(next);
    try {
      window.localStorage.setItem(seenKey, JSON.stringify([...next]));
    } catch {
      // Storage may be unavailable (a private window); the bell still counts for this visit.
    }
  };
  const item = (d: Decision) => (
    <li key={d.workItem}>
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          notices?.open(d);
        }}
      >
        <b data-pack="">{d.name}</b>
        <span className="small muted">
          {/* What it is about (#513), else who it is with. */}
          {d.subject || d.counterparty ? <span translate="no">{d.subject ?? d.counterparty?.name}</span> : null}
          {(d.subject || d.counterparty) && d.late ? " · " : null}
          {d.late ? w.shellLateWork : null}
        </span>
      </button>
    </li>
  );
  return (
    <div
      ref={wrap}
      className="menu-wrap"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        event.stopPropagation();
        setOpen(false);
        button.current?.focus();
      }}
    >
      <button
        ref={button}
        type="button"
        className="iconbtn"
        aria-label={count > 0 ? w.shellNotificationsCount(count) : w.shellNotifications}
        aria-expanded={open}
        aria-controls={open ? "notifications" : undefined}
        onClick={() => {
          if (!open) openBell();
          setOpen(!open);
        }}
      >
        <Icon name="bell" />
        {count > 0 ? (
          <span className="badge" aria-hidden="true">
            {count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div id="notifications" className="menu popover" role="region" aria-label={w.shellNotifications}>
          {shown.fresh.length + shown.late.length + shown.messages.length === 0 ? <p>{w.shellNothingNew}</p> : null}
          {shown.fresh.length > 0 ? (
            <>
              <p className="menu-head small muted">{w.shellNewWork}</p>
              <ul aria-label={w.shellNewWork}>{shown.fresh.map(item)}</ul>
            </>
          ) : null}
          {shown.late.length > 0 ? (
            <>
              <p className="menu-head small muted">{w.shellLateWork}</p>
              <ul aria-label={w.shellLateWork}>{shown.late.map(item)}</ul>
            </>
          ) : null}
          {shown.messages.length > 0 ? (
            <>
              <p className="menu-head small muted">{w.shellNewMessages}</p>
              <ul aria-label={w.shellNewMessages}>
                {shown.messages.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        notices?.openChat?.();
                      }}
                    >
                      <span className="small">{glimpse(m.text)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {notices?.emails ? <EmailChoice api={notices.emails} /> : null}
        </div>
      ) : null}
    </div>
  );
}

type Found =
  | { readonly state: "idle" }
  | { readonly state: "searching" }
  | { readonly state: "failed" }
  | { readonly state: "found"; readonly words: string; readonly results: readonly SearchResult[] };

function Result({ result, onOpen }: { result: SearchResult; onOpen: () => void }) {
  const w = useWords();
  const inner = (
    <>
      <span className="small muted">{w[RESULT_KIND[result.kind]]}</span>
      <b>{result.title}</b>
      {result.detail ? <span className="small">{result.detail}</span> : null}
    </>
  );
  // A Document opens its PDF; an order its Run, and a Counterparty its list, in the Cockpit.
  if (result.kind === "document" && result.pdf)
    return (
      <a className="result" href={result.pdf} target="_blank" rel="noopener" onClick={onOpen}>
        {inner}
      </a>
    );
  return (
    <button type="button" className="result" onClick={onOpen}>
      {inner}
    </button>
  );
}

function Search({
  onSearch,
  onOpenResult,
}: {
  onSearch: (words: string) => Promise<readonly SearchResult[]>;
  onOpenResult: (result: SearchResult) => void;
}) {
  const w = useWords();
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [found, setFound] = useState<Found>({ state: "idle" });
  const asked = useRef(0);
  const open = found.state === "found";
  useDismiss(open, wrap, () => setFound({ state: "idle" }));
  // `/` jumps to search from anywhere but a field being typed in or an open panel.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      // A modal panel keeps focus: `/` must not pull it out to the search behind.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest("input, textarea, select") !== null)
      )
        return;
      event.preventDefault();
      input.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const search = (words: string) => {
    if (words === "") return;
    const mine = ++asked.current;
    setFound({ state: "searching" });
    onSearch(words).then(
      (results) => {
        if (mine === asked.current) setFound({ state: "found", words, results });
      },
      () => {
        if (mine === asked.current) setFound({ state: "failed" });
      },
    );
  };
  const status =
    found.state === "searching"
      ? w.shellSearching
      : found.state === "failed"
        ? w.shellSearchFailed
        : found.state === "found" && found.results.length === 0
          ? w.shellNoMatches
          : "";
  return (
    <div
      ref={wrap}
      className="search-wrap"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || found.state === "idle") return;
        event.stopPropagation();
        setFound({ state: "idle" });
        input.current?.focus();
      }}
    >
      <form
        role="search"
        className="search"
        onSubmit={(event) => {
          event.preventDefault();
          search(input.current?.value.trim() ?? "");
        }}
      >
        <Icon name="search" />
        <label className="sr-only" htmlFor="search">
          {w.shellSearch}
        </label>
        <input
          ref={input}
          id="search"
          type="search"
          autoComplete="off"
          aria-keyshortcuts="/"
          aria-controls={open && found.results.length > 0 ? "search-results" : undefined}
          placeholder={w.shellSearchHint}
          onChange={() => {
            asked.current += 1;
            setFound({ state: "idle" });
          }}
        />
        <kbd aria-hidden="true">/</kbd>
      </form>
      <p role="status" className="search-note">
        {status}
      </p>
      {open && found.results.length > 0 ? (
        <div id="search-results" className="search-results" role="region" aria-label={w.shellSearchResults}>
          <ul>
            {found.results.map((result) => (
              <li key={`${result.kind}:${result.id}`}>
                <Result
                  result={result}
                  onOpen={() => {
                    setFound({ state: "idle" });
                    onOpenResult(result);
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function Shell({
  session,
  owner,
  opens,
  page,
  onNavigate,
  onSignOut,
  onSearch,
  onOpenResult,
  sidebarEnd,
  trialDaysLeft,
  notice,
  notices,
  children,
}: {
  session: Session;
  owner: boolean;
  /** The pages the person's Roles open, as the Application API answers (#513): the sidebar lists only those. */
  opens?: ReadonlySet<string> | undefined;
  page: Page;
  onNavigate: (page: Page) => void;
  onSignOut: () => void;
  /** The top bar's search: orders, Counterparties and Documents. */
  onSearch: (words: string) => Promise<readonly SearchResult[]>;
  onOpenResult: (result: SearchResult) => void;
  /** At the foot of the sidebar: pausing LAMP's agents. */
  sidebarEnd?: ReactNode;
  /** Whole days left in the trial, shown to an Owner beside Credits while it runs (#211). */
  trialDaysLeft?: number | undefined;
  /** Above every page: why LAMP's agents are paused. */
  notice?: ReactNode;
  /** What the bell lists. */
  notices?: Notices;
  children: ReactNode;
}) {
  const w = useWords();
  const { language, setLanguage } = useLanguage();
  const main = useRef<HTMLElement>(null);
  const first = useRef(true);
  const menuTab = useRef<HTMLButtonElement>(null);
  const drawerClose = useRef<HTMLButtonElement>(null);
  const [closed, setClosed] = useState(readClosed);
  const [drawer, setDrawer] = useState(false);

  // After moving to another page, focus goes to its content, as a page load would put it.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    main.current?.focus();
  }, [page]);
  useEffect(() => {
    document.title = w.shellTitle(w[PAGES[page].title]);
  });
  // Focus moves once the render has made the drawer, or the page behind it, no longer inert.
  const returnToMenu = useRef(false);
  useEffect(() => {
    if (drawer) drawerClose.current?.focus();
    else if (returnToMenu.current) menuTab.current?.focus();
    returnToMenu.current = false;
  }, [drawer]);
  // A drawer left open while the window widens past phone size would keep the page inert.
  useEffect(() => {
    const phone = typeof window.matchMedia === "function" ? window.matchMedia(PHONE) : undefined;
    const onChange = () => {
      if (!phone?.matches) setDrawer(false);
    };
    phone?.addEventListener("change", onChange);
    return () => phone?.removeEventListener("change", onChange);
  }, []);

  const closeDrawer = () => {
    returnToMenu.current = true;
    setDrawer(false);
  };
  const navigate = (next: Page) => {
    // The current page again: nothing moves focus to the content, so it goes back to Menu.
    if (drawer && next === page) returnToMenu.current = true;
    setDrawer(false);
    onNavigate(next);
  };
  const toggleGroup = (id: string) => {
    const next = new Set(closed);
    if (!next.delete(id)) next.add(id);
    setClosed(next);
    try {
      window.localStorage.setItem(CLOSED_GROUPS, JSON.stringify([...next]));
    } catch {
      // Storage may be unavailable (a private window); the groups still open and close.
    }
  };
  const switchBusiness = () => {
    setDrawer(false);
    onSignOut();
  };

  return (
    <div className={drawer ? "app drawer-open" : "app"}>
      <a className="skip" href="#main" inert={drawer}>
        {w.shellSkip}
      </a>
      {drawer ? <div className="scrim" onClick={closeDrawer} aria-hidden="true" /> : null}
      <nav
        id="sidebar"
        className="sidebar"
        aria-label="LAMP"
        onKeyDown={(event) => {
          if (event.key === "Escape" && drawer) closeDrawer();
        }}
      >
        <div className="brand-row">
          <p className="brand">LAMP</p>
          <button ref={drawerClose} type="button" className="btn drawer-close" onClick={closeDrawer}>
            {w.shellClose}
          </button>
        </div>
        <Menu
          id="business-menu"
          label={w.shellSwitchBusiness}
          className="switch"
          items={[
            session.businessName !== undefined
              ? { label: session.businessName, translate: "no", checked: true, onSelect: () => {} }
              : { label: w.shellBusiness, checked: true, onSelect: () => {} },
            { label: w.shellOtherBusiness, onSelect: switchBusiness },
          ]}
        >
          <span>
            {/* The business by its name, never its id (#343): a proper noun no language translates. "Your business"
                only where the api does not know it. */}
            {session.businessName !== undefined ? <span translate="no">{session.businessName}</span> : w.shellBusiness}
            {/* Role names are the pack's own words, which the packs translate (#220). */}
            {session.roles.length > 0 ? (
              <small>
                <span data-pack="">{session.roles.join(", ")}</span>
              </small>
            ) : null}
          </span>
          <Icon name="chevron" />
        </Menu>
        {NAV.map((group) => {
          const open = !closed.has(group.id);
          const pages = group.pages.filter((p) => listed(p, owner, opens));
          // A group with no page the person's Roles open is not listed at all.
          if (pages.length === 0) return null;
          return (
            <div className="group" key={group.id}>
              <button
                type="button"
                className="nav-group"
                aria-expanded={open}
                aria-controls={`nav-${group.id}`}
                onClick={() => toggleGroup(group.id)}
              >
                {w[group.label]}
                <Icon name="chevron" />
              </button>
              <ul id={`nav-${group.id}`} className="nav-list" hidden={!open}>
                {pages.map((p) => (
                  <li key={p}>
                    <NavLink page={p} current={page} onNavigate={navigate}>
                      {w[PAGES[p].title]}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        <div className="nav-foot">
          {owner ? (
            <ul className="nav-list">
              <li>
                <NavLink page="credits" current={page} onNavigate={navigate}>
                  {w.shellCredits}
                </NavLink>
              </li>
              {trialDaysLeft !== undefined ? (
                <li>
                  <NavLink page="billing" current={page} onNavigate={navigate}>
                    <span className="trial-left" data-urgent={trialDaysLeft <= 7}>
                      {w.shellTrialLeft(trialDaysLeft)}
                    </span>
                  </NavLink>
                </li>
              ) : null}
            </ul>
          ) : null}
          {/* Pausing LAMP's agents (the Kill Switch): its panel opens over the drawer on phones, which stays open
              beneath it. */}
          {sidebarEnd}
        </div>
      </nav>
      <header className="topbar" inert={drawer}>
        <h1>{w[PAGES[page].title]}</h1>
        <div className="tools">
          <Search onSearch={onSearch} onOpenResult={onOpenResult} />
          <Notifications notices={notices} person={session.personId} inChat={page === "chat"} />
          <Menu
            id="account-menu"
            label={w.shellAccount}
            className="btn"
            header={
              session.roles.length > 0 ? (
                <p className="menu-head small muted" data-pack="">
                  {session.roles.join(", ")}
                </p>
              ) : null
            }
            items={[
              {
                label: w.shellOtherLanguage,
                lang: otherLanguage(language),
                onSelect: () => setLanguage(otherLanguage(language)),
              },
              { label: w.shellPreferences, onSelect: () => onNavigate("settings") },
              { label: w.shellSwitchBusiness, onSelect: switchBusiness },
              { label: w.shellSignOut, onSelect: onSignOut },
            ]}
          >
            {w.shellAccount}
          </Menu>
        </div>
      </header>
      <main id="main" ref={main} tabIndex={-1} inert={drawer}>
        {notice}
        {children}
        <LegalLinks />
      </main>
      <nav className="tabbar" aria-label={w.shellTabs} inert={drawer}>
        {TABS.filter((tab) => listed(tab.page, owner, opens)).map((tab) => (
          <NavLink key={tab.page} page={tab.page} current={page} onNavigate={navigate}>
            <Icon name={tab.icon} />
            {w[tab.label]}
          </NavLink>
        ))}
        <button
          ref={menuTab}
          type="button"
          aria-expanded={drawer}
          aria-controls="sidebar"
          onClick={() => setDrawer(true)}
        >
          <Icon name="menu" />
          {w.shellMenu}
        </button>
      </nav>
    </div>
  );
}
